import * as React from "react"
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import SidebarContent from "../../Sidebar/Content"
import SidebarContainer from "../../Sidebar/SidebarContainer"
import ClassSidebar from "../General/ClassSidebar"
import TeacherAddClass from "./Forms/AddClass"
import * as teacher from "../../../shared/routes/teacher"
import { ClassResponse } from "../../../shared/types/teacherTypes"
import findById from "../../../helpers/findById"
import refreshClassesFactory from "../General/Helpers/refreshClassesFactory"
import DashboardAuth from "../General/Auth/DashboardAuth"

/*
* Page Components
*/
import TeacherOverview from "./Overview"
import TeacherAssignments from "./Assignments/TeacherAssignments"
import TeacherAssignmentSearch from "./Assignments/AssignmentSearch"
import TeacherCreateAssignment, { CreateAssignmentProps } from "./Assignments/CreateAssignment"
import TeacherEditAssignment, { EditAssignmentProps } from "./Assignments/EditAssignment"
import TeacherStudents from "./Students"
import TeacherClassSettings from "./Settings"
import TeacherAssignmentGrading from "./Assignments/AssignmentGrading"
import checkRouteState from "helpers/checkRouteState"
import Analytics from "./Analytics"
import GradeBook from "./GradeBook/GradeBook"
import LessonPlans from "./LessonPlans/LessonPlans"
import LessonPlan from "./LessonPlans/LessonPlan"
import PlanAssignmentPreview from "./LessonPlans/PlanAssignmentPreview"
import SAQLandingPage from "./SAQLandingPage"
import DemoMode from "./DemoMode/DemoMode"
import ClassMasteryPage from "./Science35/ClassMasteryPage"
import StudentMasteryPage from "./Science35/StudentMasteryPage"
import { AuthContext } from "AuthContext"
import Preview from "components/Lessons/Grading/AssignmentPreview/Preview"
import EditReviewAssignment from "./Assignments/EditReviewAssignment"
import ScienceSupportDocumentsPage from "./Assignments/ScienceSupportDocumentsPage"
import TeacherReviewAssignments from "./Assignments/TeacherReviewAssignments"
import { generateSidebarContents } from "helpers/generateSidebarContents"

export default function TeacherDashboard() {
    const { hasStemSimsAccess, hasCurriculumAccess, hasScience21Access, homeschoolBlocks, scienceBlocks } = React.useContext(AuthContext)
    const location = useLocation()
    const { id: createdClassId } = location?.state ? location.state as { id?: string } : { id: undefined } // new class from teacher onboarding quiz
    const [classes, setClasses] = React.useState<ClassResponse[]>([])
    const [createClassShown, setCreateClassShown] = React.useState(false)
    const [activeClass, setActiveClass] = React.useState<ClassResponse>(null)
    const [loading, setLoading] = React.useState(true)
    const refreshClasses = refreshClassesFactory({
        getClasses: teacher.getClasses,
        setLoading,
        setClasses,
        setActiveClass
    })

    const linkGroups = generateSidebarContents({ hasStemSimsAccess, hasCurriculumAccess, hasScience21Access, homeschoolBlocks, scienceBlocks })

    const validateGradingRoute = (routeStateClass, Component, url="/dashboard/teacher/assignments") => {
        const validStateSubmission = checkRouteState(location?.state, routeStateClass)

        if(validStateSubmission) {
            location.state = validStateSubmission
            return Component
        }
        return <Navigate to={url} /> 
    }

    const validateFeatureAccess = ({ hasFeatureAccess, Component, redirectUrl } : { hasFeatureAccess: boolean, Component: React.ReactNode, redirectUrl: string }) => {
        if (hasFeatureAccess) {
            return Component
        }
        return <Navigate to={redirectUrl} /> 
    }

    // Component did mounted
    React.useEffect(() => {
        if (createdClassId) {
            void refreshClasses()
            .then((refreshedClasses) => {
                setActiveClass(findById(createdClassId, refreshedClasses))
            })
        } else {
            void refreshClasses()
        }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [])

    // Cache active class
    React.useEffect(() => {
        if (activeClass) {
            localStorage.setItem("activeClass", JSON.stringify(activeClass))
        }
    }, [activeClass])

    const hasScienceAccess = scienceBlocks.length > 0
    const hasTextbookAccess = hasScience21Access || homeschoolBlocks.length > 0

    return (
        <SidebarContainer>
            <ClassSidebar
                activeClass={activeClass}
                classes={classes}
                addClassLabel={"+ Add Class"}
                onAddClass={() => setCreateClassShown(true)}
                onClassSelect={(selectedClass) => setActiveClass(selectedClass)}
                linkGroups={linkGroups}
            />

            <TeacherAddClass
                activeClass={activeClass}
                show={createClassShown}
                onHide={() => setCreateClassShown(false)}
                classCreated={(newClass) => {
                    setCreateClassShown(false)
                    void refreshClasses()
                        .then((refreshedClasses) => {
                            setActiveClass(findById(newClass.id, refreshedClasses))
                        })
                }}
            />
            
            <SidebarContent>
                <DashboardAuth
                    activeClass={activeClass}
                    loading={loading}
                    redirectUrl="/dashboard/teacher/overview"
                    permission="TEACHER"
                >
                    <Routes>
                        <Route
                            path="/overview"
                            element={
                                <TeacherOverview
                                    activeClass={activeClass}
                                    classes={classes}
                                    onAddClass={() => setCreateClassShown(true)}
                                    onClassSelected={(selectedClass) => {
                                        setActiveClass(findById(selectedClass.id, classes))
                                    }}
                                />
                            }
                        />
                        <Route path="/assignments" element={<TeacherAssignments activeClass={activeClass} refreshClasses={refreshClasses} />} />
                        <Route path="/assignments/search" element={<TeacherAssignmentSearch activeClass={activeClass} />} />
                        <Route path="/assignments/add" element={validateGradingRoute(CreateAssignmentProps, <TeacherCreateAssignment activeClass={activeClass} />)}/>
                        <Route path="/assignments/edit" element={validateGradingRoute(EditAssignmentProps, <TeacherEditAssignment activeClass={activeClass} />)}/>
                        <Route path="/assignments/grading/*" element={<TeacherAssignmentGrading activeClass={activeClass} />} />
                        <Route path="/assignments/:assignmentId/demo-mode" element={<DemoMode activeClass={activeClass} />} />
                        <Route path="/students" element={<TeacherStudents activeClass={activeClass} refreshClasses={refreshClasses} />} />
                        <Route path="/gradebook" element={<GradeBook activeClass={activeClass} refreshClasses={refreshClasses}/>} />
                        <Route path="/analytics" element={<Analytics activeClass={activeClass} />} />
                        <Route path="/lesson-plans" element={<LessonPlans />} />
                        <Route path="/lesson-plans/:planId" element={<LessonPlan activeClass={activeClass} />} />
                        <Route path="/plan-assignment/:assignmentId/preview" element={<PlanAssignmentPreview />} />
                        <Route path="/settings" element={<TeacherClassSettings activeClass={activeClass} refreshClasses={refreshClasses} />} />
                        <Route path="/saq" element={<SAQLandingPage activeClass={activeClass} />} />

                        <Route path="/textbook/:textbookProduct/documents" element={validateFeatureAccess({ hasFeatureAccess: hasTextbookAccess, Component: <ScienceSupportDocumentsPage />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/textbook/:textbookProduct/assignments" element={validateFeatureAccess({ hasFeatureAccess: hasTextbookAccess, Component: <TeacherAssignments activeClass={activeClass} refreshClasses={refreshClasses} />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/textbook/:textbookProduct/gradebook" element={validateFeatureAccess({ hasFeatureAccess: hasTextbookAccess, Component: <GradeBook activeClass={activeClass} refreshClasses={refreshClasses}/>, redirectUrl: "/dashboard/teacher/overview" })} />

                        <Route path="/science/:block/assignments" element={validateFeatureAccess({ hasFeatureAccess: hasScienceAccess, Component: <TeacherReviewAssignments activeClass={activeClass} />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/science/:block/class-mastery" element={validateFeatureAccess({ hasFeatureAccess: hasScienceAccess, Component: <ClassMasteryPage activeClass={activeClass} />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/science/:block/student-mastery" element={validateFeatureAccess({ hasFeatureAccess: hasScienceAccess, Component: <StudentMasteryPage activeClass={activeClass} />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/science/:block/documents" element={validateFeatureAccess({ hasFeatureAccess: hasScienceAccess, Component: <ScienceSupportDocumentsPage />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/science/:block/assignments/preview" element={validateFeatureAccess({ hasFeatureAccess: hasScienceAccess, Component: <Preview />, redirectUrl: "/dashboard/teacher/overview" })} />
                        <Route path="/science/:block/assignments/edit" element={validateGradingRoute(EditAssignmentProps, <EditReviewAssignment activeClass={activeClass} />)} />
    
                        <Route path="*" element={<Navigate to="/dashboard/teacher/overview" replace={true} />} />
                    </Routes>
                </DashboardAuth>
            </SidebarContent>
        </SidebarContainer>
    )
}
